import PolicyContainer from "./PolicyContainer";
import { Link } from "../general";
import { 
    PROJECT_NAME,
    PROJECT_DOMAIN,
    CONTACT_EMAIL,
} from "../../config";

const LAST_UPDATED_DATE = '19.02.2025';

const PrivacyPolicy = () => {
    return (
        <PolicyContainer 
            title='Privacy Policy'
            body={
                <>
                    Last Updated: {LAST_UPDATED_DATE}<br/><br/>

                    <strong>{PROJECT_NAME}</strong> (<Link href={`https://${PROJECT_DOMAIN}`}>{PROJECT_DOMAIN}</Link>) is committed to protecting your privacy. This Privacy Policy explains how we collect, process, store, and protect personal data in compliance with the General Data Protection Regulation (GDPR) (EU 2016/679).
                    By using <strong>{PROJECT_NAME}</strong>, you agree to this Privacy Policy. If you do not agree, please discontinue use of the service.

                    <h2>1. Data Controller</h2>
                    <strong>{PROJECT_NAME}</strong> is the data controller for processing personal data.
                    For any inquiries regarding your data, please contact us at <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.

                    <h2>2. Data We Collect</h2>
                    We collect the following types of data:

                    <h3>2.1. Account Information</h3>
                    <ul>
                        <li>Email Address</li>
                        <li>Username</li>
                        <li>Password (stored securely as a cryptographic hash)</li>
                    </ul>

                    <h3>2.2. Conversions & Redirects </h3>
                    <ul>
                        <li>Shortened URLs and their corresponding original URLs</li>
                        <li>Redirect data, which may include:</li>
                        <ul>
                            <li>IP address</li>
                            <li>Browser type and version</li>
                            <li>Device type</li>
                            <li>Operating system</li>
                            <li>Timestamp of access</li>
                            <li>Other metadata relevant to redirects and user interactions</li>
                        </ul>
                    </ul>

                    <h3>2.3. Statistics & Analytics</h3>
                    <ul>
                        <li>Number of redirects and conversions</li>
                        <li>Types of conversions</li>
                        <li>Other aggregated insights related to link performance and user activity</li>
                    </ul>

                    <h3>2.4. Communications Data</h3>
                    <ul>
                        <li>User inquiries and support requests</li>
                    </ul>

                    <h3>2.5. Cookies & Tracking (Google Analytics)</h3>
                    We use Google Analytics and similar tools to collect:
                    <ul>
                        <li>Page visits, navigation behavior, and time spent on site</li>
                        <li>Device, browser, and approximate location (anonymized where required)</li>
                        <li>Traffic sources (how users arrive at <strong>{PROJECT_NAME}</strong>)</li>
                    </ul>
                    All data is stored and processed within the European Union, ensuring compliance with GDPR 
                    data protection standards. Users can opt out of tracking via browser settings or disable 
                    cookies (see Section 8).

                    <h2>3. How We Use Your Data</h2>
                    We process data for the following purposes, in compliance with GDPR:
                    <br/><br/>
                    <table>
                        <tr>
                            <th>Purpose</th>
                            <th>Lawful Basis (GDPR)</th>
                        </tr>
                        <tr>
                            <td>Creating and managing accounts</td>
                            <td>Contractual necessity (Art. 6(1)(b))</td>
                        </tr>
                        <tr>
                            <td>Providing URL shortening, redirection, and analytics services</td>
                            <td>Contractual necessity (Art. 6(1)(b))</td>
                        </tr>
                        <tr>
                            <td>Enhancing service performance & security</td>
                            <td>Legitimate interest (Art. 6(1)(f))</td>
                        </tr>
                        <tr>
                            <td>Monitoring and preventing abuse (spam, fraud, illegal content)</td>
                            <td>Legitimate interest (Art. 6(1)(f))</td>
                        </tr>
                        <tr>
                            <td>Responding to user support requests</td>
                            <td>Legitimate interest (Art. 6(1)(f))</td>
                        </tr>
                        <tr>
                            <td>Using Google Analytics for performance insights</td>
                            <td>Legitimate interest (Art. 6(1)(f))</td>
                        </tr>
                        <tr>
                            <td>Legal compliance (e.g., responding to lawful requests)</td>
                            <td>Legal obligation (Art. 6(1)(c))</td>
                        </tr>
                    </table>

                    <h2>4. Data Security</h2>
                    <ul>
                        <li>Encryption of sensitive data at rest and in transit</li>
                        <li>Access restrictions to prevent unauthorized data access</li>
                        <li>Hashed passwords for secure authentication</li>
                        <li>Secure communication via HTTPS</li>
                        <li>Controlled API access to ensure only authorized sources interact with the system</li>
                    </ul>

                    <h2>5. Data Retention Policy</h2>
                    We retain data as long as necessary for business, security, and legal purposes:
                    <br/><br/>
                    <table>
                        <tr>
                            <th>Data Type</th>
                            <th>Retention Period</th>
                        </tr>
                        <tr>
                            <td>User Accounts (active)</td>
                            <td>Until user requests deletion</td>
                        </tr>
                        <tr>
                            <td>Deactivated Accounts</td>
                            <td>Retained indefinitely, unless deletion is requested</td>
                        </tr>
                        <tr>
                            <td>Shortened URLs & Redirect Data</td>
                            <td>Indefinitely</td>
                        </tr>
                        <tr>
                            <td>Analytics & Statistics</td>
                            <td>Indefinitely</td>
                        </tr>
                        <tr>
                            <td>Google Analytics Data</td>
                            <td>Per Google’s retention policies</td>
                        </tr>
                        <tr>
                            <td>Customer Support Requests</td>
                            <td>2 years</td>
                        </tr>
                    </table>
                    <br/>
                    Users can request account deletion, but shortened URLs and analytics remain stored unless legally required to be deleted.

                    <h2>6. User Rights (GDPR Compliance)</h2>
                    <ul>
                        <li><b>Right to Access</b> – Request a copy of your data.</li>
                        <li><b>Right to Rectification</b> – Update email, username, or password.</li>
                        <li><b>Right to Erasure ("Right to be Forgotten")</b> – Delete your account.</li>
                        <li><b>Right to Restrict Processing</b> – Limit how your data is used.</li>
                        <li><b>Right to Data Portability</b> – Receive your data in a structured format.</li>
                        <li><b>Right to Object</b> – Stop data processing in certain cases.</li>
                        <li><b>Right to Lodge a Complaint</b> – File a complaint with your Data Protection Authority (DPA).</li>
                    </ul>
                    To exercise your rights, email us at <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.

                    <h2>7. Data Sharing & Third-Party Services</h2>
                    We do <strong>not</strong> sell personal data. However, we may share limited data with:
                    <ul>
                        <li>Hosting & Database Providers (for secure storage)</li>
                        <li>Google Analytics (for traffic insights)</li>
                        <li>Legal Authorities (if required by law)</li>
                    </ul>
                    All third-party services follow GDPR compliance and security standards.

                    <h2>8. Cookies & Tracking</h2>
                    We use cookies to improve user experience and analyze traffic. These may include:
                    <ul>
                        <li>Essential Cookies (necessary for service functionality)</li>
                        <li>Performance Cookies (used by Google Analytics to measure site usage)</li>
                    </ul>
                    <h3>Managing Cookies</h3>
                    Users can disable cookies via browser settings or opt out of Google Analytics tracking using Google’s opt-out tools.

                    <h2>9. Account Deactivation & Deletion</h2>
                    Users can deactivate their accounts at any time. Account data is retained indefinitely unless a deletion request is made.
                    To delete your account, contact us at <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.

                    <h2>10. Changes to This Policy</h2>
                    We may update this Privacy Policy as needed. If significant changes occur, users will be notified via email or website notice.
                </>
            }
        />
    );
}

export default PrivacyPolicy;